/**
 * nodePickResolver — INIT-2026-Q3-SCENE-HIERARCHY geometry-binding phase (viewer).
 *
 * The UNIFIED pick resolver: the single place that translates any 3D-engine
 * pick (by glTF node name) or scene-tree click (by UniversalSceneNode) into
 * the canonical selection contract.
 *
 * Binding key (confirmed):
 *   assimp OBJ-group → glTF node name === TreeNode.standardRef.nativeId
 *   Three.js / Giro3D hit Object3D.name === glTF node name
 *
 * Design constraints (OBJECT_IDENTITY_AND_SELECTION_POLICY §7, DESIGN_POLICY §5):
 *   - Pure functions, no side effects, no domain branching.
 *   - Format-agnostic: resolver looks up by name, returns a TreeNode.
 *   - No new dependencies.
 *
 * Source: INIT-2026-Q3-SCENE-HIERARCHY geometry-binding spec.
 */

import type {
  ObjectIndex,
  ObjectIndexRecord,
  UniversalSceneNode,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type { SceneSelection } from "@/app/sceneSelection";
import { selectByIndexNodeId } from "@/app/sceneSelection";

// ─────────────────────────────────────────────────────────────────────────────
// PickContext
// ─────────────────────────────────────────────────────────────────────────────

export interface PickContext {
  /** The loaded object-index (from the current gTwin's payload). */
  readonly objectIndex: ObjectIndex | null;
  /** Pre-built lookup from nativeId → TreeNode (built from objectIndex). */
  readonly nodeByNativeId: ReadonlyMap<string, UniversalSceneNode>;
  /** Pre-built lookup from featureId → TreeNode (built from objectIndex). */
  readonly nodeByFeatureId: ReadonlyMap<string, UniversalSceneNode>;
  /** Pre-built lookup from objectId → TreeNode (built from objectIndex). */
  readonly nodeById: ReadonlyMap<string, UniversalSceneNode>;
  /**
   * Layer-scoped feature lookup: key = `${layerId}:${featureId}`.
   * Resolves batch-id collisions across layers (same batchId in different tilesets).
   */
  readonly nodeByLayerFeature: ReadonlyMap<string, UniversalSceneNode>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert a flat ObjectIndexRecord (query-optimised form) to a
 * UniversalSceneNode (tree-node form). Only the fields that exist on
 * ObjectIndexRecord are populated; viewer-only fields default to safe values.
 */
function recordToNode(rec: ObjectIndexRecord): UniversalSceneNode {
  // Widen to access optional USN fields (metadata, aspects, etc.)
  const recAny = rec as ObjectIndexRecord & {
    metadata?: Record<string, unknown>;
    aspects?: UniversalSceneNode["aspects"];
  };
  return {
    objectId: rec.objectId,
    id: rec.objectId,
    parentId: rec.parentObjectId,
    lor: rec.lor,
    label: rec.label,
    kind: rec.kind,
    layerId: rec.layerId,
    featureId: rec.featureId,
    domain: rec.domain,
    standardRef: rec.standardRef,
    metadata: recAny.metadata ?? {},
    hasChildren: false,
    sortKey: 0,
    aspects: recAny.aspects ?? { spatial: { parentId: rec.parentObjectId } },
    ...(rec.bounds3d !== undefined ? { bounds3d: rec.bounds3d } : {}),
    ...(rec.thumbnailUrl !== undefined ? { thumbnailUrl: rec.thumbnailUrl } : {}),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a PickContext from the gTwin's ObjectIndex.
 * Returns a context with empty maps when `objectIndex` is null so callers
 * can call `resolveGeometryPick` unconditionally and receive `null` on miss.
 */
export function buildPickContext(objectIndex: ObjectIndex | null): PickContext {
  if (!objectIndex) {
    return {
      objectIndex: null,
      nodeByNativeId: new Map(),
      nodeByFeatureId: new Map(),
      nodeById: new Map(),
      nodeByLayerFeature: new Map(),
    };
  }

  const nodeByNativeId = new Map<string, UniversalSceneNode>();
  const nodeByFeatureId = new Map<string, UniversalSceneNode>();
  const nodeById = new Map<string, UniversalSceneNode>();
  const nodeByLayerFeature = new Map<string, UniversalSceneNode>();

  for (const record of objectIndex.objects) {
    const node = recordToNode(record);
    nodeById.set(record.objectId, node);
    if (record.standardRef?.nativeId) {
      nodeByNativeId.set(record.standardRef.nativeId, node);
    }
    if (node.featureId !== null && node.featureId !== undefined) {
      nodeByFeatureId.set(String(node.featureId), node);
      if (node.layerId) {
        nodeByLayerFeature.set(`${node.layerId}:${node.featureId}`, node);
      }
    }
  }

  return { objectIndex, nodeByNativeId, nodeByFeatureId, nodeById, nodeByLayerFeature };
}

/**
 * Build a PickContext directly from an array of UniversalSceneNodes (the
 * manifest nodes). This is the source-agnostic path: it works whether the
 * hierarchy was delivered inline (objectIndex prop) OR by reference
 * (hierarchyUrl → urlObjectIndexManifest), because both end up as manifest
 * nodes. Without this, by-URL hierarchies (e.g. CityGML) produce an empty
 * pick context and neither tree→3D nor 3D→tree linking works.
 */
export function buildPickContextFromNodes(
  nodes: readonly UniversalSceneNode[],
): PickContext {
  const nodeByNativeId = new Map<string, UniversalSceneNode>();
  const nodeByFeatureId = new Map<string, UniversalSceneNode>();
  const nodeById = new Map<string, UniversalSceneNode>();
  const nodeByLayerFeature = new Map<string, UniversalSceneNode>();

  for (const node of nodes) {
    nodeById.set(node.objectId, node);
    if (node.standardRef?.nativeId) {
      nodeByNativeId.set(node.standardRef.nativeId, node);
    }
    if (node.featureId !== null && node.featureId !== undefined) {
      nodeByFeatureId.set(String(node.featureId), node);
      if (node.layerId) {
        nodeByLayerFeature.set(`${node.layerId}:${node.featureId}`, node);
      }
    }
  }

  return { objectIndex: null, nodeByNativeId, nodeByFeatureId, nodeById, nodeByLayerFeature };
}

/**
 * Given a hit Object3D name (glTF node name) from the 3D engine, returns
 * the matching TreeNode (by standardRef.nativeId) and a SceneSelection.
 * Returns null when no match (e.g. basemap, terrain, non-hierarchy object).
 *
 * The returned selection has `source: "engine-pick"` so audit consumers can
 * distinguish programmatic tree clicks from geometry picks.
 */
/**
 * Normalise a feature id coming from the 3D engine so it matches the value
 * stored on the object-index node. The Giro3D/3d-tiles-renderer pick path
 * returns the batch id prefixed (e.g. `feature:771`), while the converter
 * writes the bare batch index (`771`). We try the raw value first, then the
 * de-prefixed value, so both representations resolve.
 */
function featureIdCandidates(raw: string): string[] {
  const out = [raw];
  const colon = raw.lastIndexOf(":");
  if (colon >= 0) out.push(raw.slice(colon + 1));
  return out;
}

export function resolveGeometryPick(
  hitName: string | null,
  hitFeatureIdOrCtx: string | null | PickContext,
  maybeCtx?: PickContext,
  hitLayerId?: string | null,
): { node: UniversalSceneNode; selection: SceneSelection } | null {
  const hitFeatureId =
    typeof hitFeatureIdOrCtx === "string" || hitFeatureIdOrCtx === null
      ? hitFeatureIdOrCtx
      : null;
  const ctx =
    maybeCtx ??
    (typeof hitFeatureIdOrCtx === "object" ? hitFeatureIdOrCtx : null);
  if (!ctx) return null;

  let node: UniversalSceneNode | null = null;

  // Layer-scoped lookup first (resolves batch-id collisions across layers)
  if (hitFeatureId && hitLayerId && ctx.nodeByLayerFeature.size > 0) {
    for (const candidate of featureIdCandidates(String(hitFeatureId))) {
      const match = ctx.nodeByLayerFeature.get(`${hitLayerId}:${candidate}`);
      if (match) {
        node = match;
        break;
      }
    }
  }

  // Fallback: global featureId lookup
  if (!node && hitFeatureId) {
    for (const candidate of featureIdCandidates(String(hitFeatureId))) {
      const match = ctx.nodeByFeatureId.get(candidate);
      if (match) {
        node = match;
        break;
      }
    }
  }
  if (!node && hitName) {
    node = ctx.nodeByNativeId.get(hitName) ?? null;
  }
  if (!node) return null;

  const selection = selectByIndexNodeId(node.objectId, {
    source: "engine-pick",
    layerId: node.layerId ?? undefined,
    featureId:
      node.featureId !== null && node.featureId !== undefined
        ? node.featureId
        : undefined,
    nodeKind: node.kind,
    sceneItemId: node.layerId ?? undefined,
  });

  return { node, selection };
}

// ─────────────────────────────────────────────────────────────────────────────
// G8 — panorama depth-pick → spatial resolve (survey / project frame)
// ─────────────────────────────────────────────────────────────────────────────

/** Surveying-frame XYZ in meters (+X East, +Y North, +Z Up). */
export interface SurveyXYZ {
  x: number;
  y: number;
  z: number;
}

type SurveyAabb = readonly [
  minX: number,
  minY: number,
  minZ: number,
  maxX: number,
  maxY: number,
  maxZ: number,
];

function readSurveyAabb(node: UniversalSceneNode): SurveyAabb | null {
  const meta =
    node.metadata && typeof node.metadata === "object"
      ? (node.metadata as Record<string, unknown>)
      : null;
  const raw = meta?.surveyBbox ?? meta?.bboxSurvey;
  if (Array.isArray(raw) && raw.length >= 6) {
    const nums = raw.slice(0, 6).map((v) => Number(v));
    if (nums.every((v) => Number.isFinite(v))) {
      return [
        Math.min(nums[0], nums[3]),
        Math.min(nums[1], nums[4]),
        Math.min(nums[2], nums[5]),
        Math.max(nums[0], nums[3]),
        Math.max(nums[1], nums[4]),
        Math.max(nums[2], nums[5]),
      ] as SurveyAabb;
    }
  }

  const bounds = node.bounds3d;
  if (!bounds?.center || !bounds.halfAxes) return null;
  const [cx, cy, cz] = bounds.center;
  const ha = bounds.halfAxes;
  // Column-major halfAxes: each triplet is a half-extent axis vector from center.
  const corners: SurveyXYZ[] = [
    { x: cx, y: cy, z: cz },
    { x: cx + ha[0], y: cy + ha[1], z: cz + ha[2] },
    { x: cx + ha[3], y: cy + ha[4], z: cz + ha[5] },
    { x: cx + ha[6], y: cy + ha[7], z: cz + ha[8] },
    { x: cx - ha[0], y: cy - ha[1], z: cz - ha[2] },
    { x: cx - ha[3], y: cy - ha[4], z: cz - ha[5] },
    { x: cx - ha[6], y: cy - ha[7], z: cz - ha[8] },
    {
      x: cx + ha[0] + ha[3] + ha[6],
      y: cy + ha[1] + ha[4] + ha[7],
      z: cz + ha[2] + ha[5] + ha[8],
    },
  ];
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (const c of corners) {
    minX = Math.min(minX, c.x);
    minY = Math.min(minY, c.y);
    minZ = Math.min(minZ, c.z);
    maxX = Math.max(maxX, c.x);
    maxY = Math.max(maxY, c.y);
    maxZ = Math.max(maxZ, c.z);
  }
  if (!Number.isFinite(minX)) return null;
  return [minX, minY, minZ, maxX, maxY, maxZ];
}

function pointInSurveyAabb(point: SurveyXYZ, bbox: SurveyAabb): boolean {
  return (
    point.x >= bbox[0] &&
    point.x <= bbox[3] &&
    point.y >= bbox[1] &&
    point.y <= bbox[4] &&
    point.z >= bbox[2] &&
    point.z <= bbox[5]
  );
}

function surveyAabbVolume(bbox: SurveyAabb): number {
  return (bbox[3] - bbox[0]) * (bbox[4] - bbox[1]) * (bbox[5] - bbox[2]);
}

/**
 * Resolve a surveying-frame world point (from `depthPicker.pickWorldFromScreen`)
 * against spatial bounds on indexed scene-tree nodes.
 *
 * Returns the smallest containing AABB match (most specific object). Uses the
 * same `SceneSelection` contract as {@link resolveGeometryPick}.
 */
export function resolveSpatialPickFromSurveyPoint(
  point: SurveyXYZ,
  ctx: PickContext,
): { node: UniversalSceneNode; selection: SceneSelection } | null {
  let bestNode: UniversalSceneNode | null = null;
  let bestVolume = Infinity;

  for (const node of ctx.nodeById.values()) {
    const bbox = readSurveyAabb(node);
    if (!bbox || !pointInSurveyAabb(point, bbox)) continue;
    const volume = surveyAabbVolume(bbox);
    if (volume < bestVolume) {
      bestVolume = volume;
      bestNode = node;
    }
  }

  if (!bestNode) return null;

  const selection = selectByIndexNodeId(bestNode.objectId, {
    source: "engine-pick",
    layerId: bestNode.layerId ?? undefined,
    featureId:
      bestNode.featureId !== null && bestNode.featureId !== undefined
        ? bestNode.featureId
        : undefined,
    nodeKind: bestNode.kind,
    sceneItemId: bestNode.layerId ?? undefined,
  });

  return { node: bestNode, selection };
}

/**
 * Given a TreeNode from a tree-click, returns the glTF node name to
 * highlight in the 3D engine.
 *
 * Resolution order (OBJECT_IDENTITY_AND_SELECTION_POLICY §3.1):
 *   1. node.standardRef.nativeId — the verbatim OBJ-group / glTF node name.
 *   2. node.layerId              — fallback for nodes without a standardRef.
 *   3. null                      — node has no geometry binding.
 */
export function resolveTreeNodeToGeometry(
  node: UniversalSceneNode,
): string | null {
  if (node.standardRef?.nativeId) return node.standardRef.nativeId;
  if (node.layerId) return node.layerId;
  return null;
}
